function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

import PropTypes from "prop-types";

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */


import React, { Fragment, useState } from 'react';
import classNames from 'classnames';
import { EuiBadge } from '../badge';
import { EuiI18n } from '../i18n';
import { EuiInnerText } from '../inner_text';
import { EuiLink } from '../link';
import { EuiPopover } from '../popover';

var limitBreadcrumbs = function limitBreadcrumbs(breadcrumbs, max, showMaxPopover, allBreadcrumbs) {
  var breadcrumbsAtStart = [];
  var breadcrumbsAtEnd = [];
  var limit = Math.min(max, breadcrumbs.length);
  var start = Math.floor(limit / 2);
  var overflowBreadcrumbs = showMaxPopover ? allBreadcrumbs.slice(start, start + breadcrumbs.length - limit) : [];

  for (var i = 0; i < limit; i++) {
    // We'll alternate with displaying breadcrumbs at the end and at the start, but be biased
    // towards breadcrumbs the end so that if max is an odd number, we'll have one more
    // breadcrumb visible at the end than at the beginning.
    var isEven = i % 2 === 0; // We're picking breadcrumbs from the front AND the back, so we treat each iteration as a
    // half-iteration.

    var normalizedIndex = Math.floor(i * 0.5);
    var indexOfBreadcrumb = isEven ? breadcrumbs.length - 1 - normalizedIndex : normalizedIndex;
    var breadcrumb = breadcrumbs[indexOfBreadcrumb];

    if (isEven) {
      breadcrumbsAtEnd.unshift(breadcrumb);
    } else {
      breadcrumbsAtStart.push(breadcrumb);
    }
  }

  var EuiBreadcrumbCollapsed = function EuiBreadcrumbCollapsed() {
    var _useState = useState(false),
        _useState2 = _slicedToArray(_useState, 2),
        isPopoverOpen = _useState2[0],
        setIsPopoverOpen = _useState2[1];

    var ellipsisButton = React.createElement(EuiI18n, {
      token: "euiBreadcrumbs.collapsedBadge.ariaLabel",
      default: "Show all breadcrumbs"
    }, function (ariaLabel) {
      return React.createElement(EuiBadge, {
        "aria-label": ariaLabel,
        onClick: function onClick() {
          return setIsPopoverOpen(!isPopoverOpen);
        },
        onClickAriaLabel: ariaLabel,
        title: "View hidden breadcrumbs",
        className: "euiBreadcrumb euiBreadcrumb__collapsedBadge"
      }, "\u2026");
    });

    if (showMaxPopover) {
      return React.createElement(Fragment, null, React.createElement(EuiPopover, {
        button: ellipsisButton,
        isOpen: isPopoverOpen,
        closePopover: function closePopover() {
          return setIsPopoverOpen(false);
        }
      }, React.createElement(EuiBreadcrumbs, {
        className: "euiBreadcrumbs__inPopover",
        breadcrumbs: overflowBreadcrumbs,
        responsive: false,
        truncate: false,
        max: 0
      })), React.createElement(EuiBreadcrumbSeparator, null));
    } else {
      return React.createElement(Fragment, null, React.createElement("div", {
        className: "euiBreadcrumb euiBreadcrumb--collapsed"
      }, "\u2026"), React.createElement(EuiBreadcrumbSeparator, null));
    }
  };

  if (max < breadcrumbs.length) {
    breadcrumbsAtStart.push(React.createElement(EuiBreadcrumbCollapsed, {
      key: "collapsed"
    }));
  }

  return [].concat(breadcrumbsAtStart, breadcrumbsAtEnd);
};

var EuiBreadcrumbSeparator = function EuiBreadcrumbSeparator() {
  return React.createElement("div", {
    className: "euiBreadcrumbSeparator"
  });
};

export var EuiBreadcrumbs = ({
  breadcrumbs,
  className,
  responsive = true,
  truncate = true,
  max = 5,
  showMaxPopover = false,
  ...rest
}) => {
  const breadcrumbElements = breadcrumbs.map((breadcrumb, index) => {
    const {
      text,
      href,
      onClick,
      truncate,
      className: breadcrumbClassName,
      ...breadcrumbRest
    } = breadcrumb;
    const isLastBreadcrumb = index === breadcrumbs.length - 1;
    const breadcrumbClasses = classNames('euiBreadcrumb', breadcrumbClassName, {
      'euiBreadcrumb--last': isLastBreadcrumb,
      'euiBreadcrumb--truncate': truncate
    });
    let link;

    if (isLastBreadcrumb && !href) {
      link = <EuiInnerText>
          {(ref, innerText) => <span ref={ref} className={breadcrumbClasses} title={innerText} aria-current="page" {...breadcrumbRest}>
              {text}
            </span>}
        </EuiInnerText>;
    } else {
      link = <EuiInnerText>
          {(ref, innerText) => <EuiLink ref={ref} color={isLastBreadcrumb ? 'text' : 'subdued'} onClick={onClick} href={href} className={breadcrumbClasses} title={innerText} {...breadcrumbRest}>
              {text}
            </EuiLink>}
        </EuiInnerText>;
    }

    let separator;

    if (!isLastBreadcrumb) {
      separator = <EuiBreadcrumbSeparator />;
    }

    return <Fragment key={index}>
        {link}
        {separator}
      </Fragment>;
  });
  const limitedBreadcrumbs = max ? limitBreadcrumbs(breadcrumbElements, max, showMaxPopover, breadcrumbs) : breadcrumbElements;
  const classes = classNames('euiBreadcrumbs', className, {
    'euiBreadcrumbs--truncate': truncate,
    'euiBreadcrumbs--responsive': responsive
  });
  return <nav aria-label="breadcrumb" className={classes} {...rest}>
      {limitedBreadcrumbs}
    </nav>;
};
EuiBreadcrumbs.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
     * Hides left most breadcrumbs as window gets smaller
     */
  responsive: PropTypes.bool,

  /**
     * Forces all breadcrumbs to single line and
     * truncates each breadcrumb to a particular width,
     * except for the last item
     */
  truncate: PropTypes.bool,

  /**
     * Condenses the inner items past the maximum set here
     * into a single ellipses item
     */
  max: PropTypes.number,

  /**
     * Allows the hidden breadcrumbs to be shown when
     * a `max` is set and the ellipsis is clicked in responsive mode.
     */
  showMaxPopover: PropTypes.bool,

  /**
     * The array of individual breadcrumbs, takes the following props.
     * `text` (node) (required): visible label of the breadcrumb,
     * `href` or `onClick`: provide only one (last breadcrumb will not apply either),
     * `truncate` (bool): Force a max-width on the breadcrumb text
     */
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    text: PropTypes.node.isRequired,
    href: PropTypes.string,
    onClick: PropTypes.func,
    truncate: PropTypes.bool
  }).isRequired).isRequired
};

try {
  // @ts-ignore
  EuiBreadcrumbs.displayName = "EuiBreadcrumbs"; // @ts-ignore

  EuiBreadcrumbs.__docgenInfo = {
    "description": "",
    "displayName": "EuiBreadcrumbs",
    "props": {
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "responsive": {
        "defaultValue": {
          value: true
        },
        "description": "Hides left most breadcrumbs as window gets smaller",
        "name": "responsive",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "truncate": {
        "defaultValue": {
          value: true
        },
        "description": "Forces all breadcrumbs to single line and\ntruncates each breadcrumb to a particular width,\nexcept for the last item",
        "name": "truncate",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "max": {
        "defaultValue": {
          value: 5
        },
        "description": "Condenses the inner items past the maximum set here\ninto a single ellipses item",
        "name": "max",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "showMaxPopover": {
        "defaultValue": {
          value: false
        },
        "description": "Allows the hidden breadcrumbs to be shown when\na `max` is set and the ellipsis is clicked in responsive mode.",
        "name": "showMaxPopover",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "breadcrumbs": {
        "defaultValue": null,
        "description": "The array of individual breadcrumbs, takes the following props.\n`text` (node) (required): visible label of the breadcrumb,\n`href` or `onClick`: provide only one (last breadcrumb will not apply either),\n`truncate` (bool): Force a max-width on the breadcrumb text",
        "name": "breadcrumbs",
        "required": true,
        "type": {
          "name": "Breadcrumb[]"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/breadcrumbs/breadcrumbs.tsx#EuiBreadcrumbs"] = {
      docgenInfo: EuiBreadcrumbs.__docgenInfo,
      name: "EuiBreadcrumbs",
      path: "src/components/breadcrumbs/breadcrumbs.tsx#EuiBreadcrumbs"
    };
} catch (__react_docgen_typescript_loader_error) {}